import React, { useCallback, useState, useEffect } from "react";
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import FlowToolbar from "./FlowToolbar";
import NodeEditor from "./NodeEditor";
import CustomNode from "./CustomNode";

const nodeTypes = { custom: CustomNode };

const FlowEditor = ({ initialNodes = [], initialEdges = [] }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (!initialNodes.length) return;

    // Mapeia os nós para organizar por id_pai
    const nodesMap = new Map();
    initialNodes.forEach((node) => {
      nodesMap.set(node.id, node);
    });

    // Distribui os nós de forma hierárquica
    const validNodes = initialNodes.map((node, index) => {
      const parentNode = nodesMap.get(node.id_pai);
      const xPos = parentNode ? parentNode.position.x + 250 : (index % 5) * 250;
      const yPos = parentNode ? parentNode.position.y + 200 : Math.floor(index / 5) * 150;

      return {
        id: String(node.id || `node-${index}`),
        type: "custom",
        position: { x: xPos, y: yPos },
        data: { 
          label: node.data?.label.length > 50 ? node.data.label.substring(0, 50) + "..." : node.data?.label || `Nó ${index + 1}` 
        },
      };
    });

    const validEdges = initialEdges.map((edge) => ({
      id: edge.id || `edge-${edge.source}-${edge.target}`,
      source: String(edge.source),
      target: String(edge.target),
    }));

    // console.log("🟢 Nós preparados:", validNodes);
    // console.log("🔵 Conexões preparadas:", validEdges);

    setNodes(validNodes);
    setEdges(validEdges);

    setTimeout(() => {
      fitView();
    }, 500);
  }, [initialNodes, initialEdges, fitView]);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (connection) => setEdges((eds) => addEdge(connection, eds)),
    []
  );

  const updateNode = (id, newData) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  return (
    <div style={{ width: "100%", height: "calc(100vh - 70px)", position: "relative" }}>
      <FlowToolbar setNodes={setNodes} />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_, node) => setSelectedNode(node)}
        fitView
      >
        <MiniMap />
        <Controls />
        <Background color="#ddd" gap={16} />
      </ReactFlow>

      <NodeEditor selectedNode={selectedNode} setSelectedNode={setSelectedNode} updateNode={updateNode} />
    </div>
  );
};

export default FlowEditor;
